import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { Environment } from "../utils/Environment";
import { fetchWithAuth, postWithUsername } from "../services/fetchWrapper";
import { postWithUserTableParams } from "../services/fetchWrapper";
import SuccessMessageComponent from "./SuccessMessageComponent";

const apiUrl = Environment.getApiUrl();

const LoadCostScoringData: React.FC = () => {
    const [form, setForm] = useState({
        support_table: "",
    });
    const username = localStorage.getItem("userName") || "";
    const [costFiles, setCostFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);

    // Hidden fields
    const [clientId, setClientId] = useState<string>("");
    const [schema, setSchema] = useState<string>("");
    const [baseTable, setBaseTable] = useState<string>("");

    // Asset ID and critical customer columns
    const [possibleColumns, setPossibleColumns] = useState<string[]>([]);
    const assetIdOptions = possibleColumns.filter(col =>
        /asset.?id/i.test(col) || /assetid/i.test(col) || /id/i.test(col)
    );
    const [assetIdColumn, setAssetIdColumn] = useState<string>("");

    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);

    // Loading state for columns
    const [columnsLoading, setColumnsLoading] = useState(false);

    useEffect(() => {
        const storedClientId = localStorage.getItem("client_id") || "";
        setClientId(storedClientId);

        const fetchHiddenFields = async () => {
            try {
                const response = await postWithUsername(`${apiUrl}/get-client-data-for-compare`, username);
                if (!response.ok) throw new Error("Failed to fetch schema/base table");
                const data = await response.json();
                setSchema("cof_data");
                setBaseTable(data.main_table_name || "");
            } catch (err) {
                setSchema("");
                setBaseTable("");
            }
        };
        fetchHiddenFields();
    }, [username, apiUrl]);

    // Fetch base asset ID column from backend
    useEffect(() => {
        const storedClientId = localStorage.getItem("client_id") || "";
        setClientId(storedClientId);

        const fetchCostColumns = async () => {
            if (!storedClientId || !baseTable) return;
            setColumnsLoading(true);
            try {
                const response = await postWithUserTableParams(`${apiUrl}/get-cost-columns`, {
                    username,
                    client_id: storedClientId,
                    job_id: localStorage.getItem("job_id") || ""
                });
                if (response.ok) {
                    const data = await response.json();
                    let cols: string[] = [];
                    if (Array.isArray(data.possible_material_columns)) {
                        cols = data.possible_material_columns.map((c: any) =>
                            typeof c === "string" ? c : c.column_name
                        );
                    }
                    setPossibleColumns(cols);

                    // Filter for assetIdOptions
                    const filtered = cols.filter(col =>
                        /asset.?id/i.test(col) || /assetid/i.test(col) || /id/i.test(col) || /objectid/i.test(col)
                    );
                    // Autofill with the first match, if any
                    if (filtered.length > 0) {
                        setAssetIdColumn(filtered[0]);
                    } else {
                        setAssetIdColumn("");
                    }
                }
            } catch {
                setPossibleColumns([]);
            } finally {
                setColumnsLoading(false);
            }
        };
        fetchCostColumns();
    }, [username, apiUrl, baseTable]);

    const handleSaveUserSelectedColumns = async () => {
        setShowSuccess(false);
        try {
            const columnsToSave = [
                {
                    client_id: clientId,
                    job_id: localStorage.getItem("job_id") || "",
                    column_name: assetIdColumn,
                    column_value: '',
                    flow_name: "cost",
                    description: "asset id value chosen by user"
                }
            ];
            const client_id = clientId;
            const response = await fetchWithAuth(`${apiUrl}/save-customer-calc-columns`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ columns: columnsToSave, client_id }),
            });
            if (!response.ok) {
                let errorMsg = "Failed to save selected columns";
                try {
                    const errorData = await response.json();
                    if (errorData.detail) errorMsg = errorData.detail;
                } catch { }
                throw new Error(errorMsg);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw err;
        }
    };

    // Extract columns from uploaded file (simulate for now)
    const handleCostFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setCostFiles(files);
            if (files.length > 0) {
                setForm(prev => ({
                    ...prev,
                    support_table: files[0].name.replace(/\.[^/.]+$/, "")
                }));
                // Simulate extracting columns from the file
                setColumnsLoading(true);
                // TODO: Replace with actual file parsing or backend call to get columns
                setTimeout(() => {
                    // Example: Replace with real column names
                    setPossibleColumns(["asset_id", "critical_customer", "other_col"]);
                    setColumnsLoading(false);
                }, 1000);
            }
        }
    };

    // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     setForm({ ...form, [e.target.name]: e.target.value });
    // };
    const newCols = ["critical_c"];
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setUploading(true);
        try {
            // Save user-selected columns first
            await handleSaveUserSelectedColumns();

            const formData = new FormData();
            formData.append("job_id", localStorage.getItem("job_id") || "");
            formData.append("username", username);
            formData.append("client_id", clientId);
            formData.append("schema", schema);
            formData.append("base_table", baseTable);
            formData.append("support_table", form.support_table);
            formData.append("join_col", assetIdColumn); // assetIdColumn is your join column
            newCols.forEach(col => formData.append("new_cols", col));
            costFiles.forEach(file => formData.append("cost_files", file));

            const response = await fetchWithAuth(`${apiUrl}/load-cof-cost-calc-data`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                let errorMsg = "Upload failed";
                try {
                    const errorData = await response.json();
                    if (errorData.detail) errorMsg = errorData.detail;
                } catch { }
                alert(errorMsg);
                setUploading(false);
                return;
            }

            // const data = await response.json();
            // setUploadResult(data);
            localStorage.setItem("costScoringDataLoaded", "true");
            setUploadStatus("completed");
            setStatusMessage("Data uploaded successfully!");
            setShowSuccess(true);
        } catch (err) {
            alert("Upload failed");
            // eslint-disable-next-line no-console
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                maxWidth: 500,
                width: "100%",
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <form
                onSubmit={handleSubmit}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    maxWidth: 500,
                    width: "100%",
                }}
            >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textAlign: "center", textTransform: "uppercase" }}>
                    Upload Hydraulic COF scores
                </Typography>
                <svg
                    width="292"
                    height="6"
                    viewBox="0 0 292 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: "block", margin: "0 auto 16px" }}
                >
                    <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
                </svg>
                {/* <TextField
                label="Support Table"
                name="support_table"
                value={form.support_table}
                onChange={handleInputChange}
                required
            /> */}
                {/* <Typography variant="caption" color="warning.main" sx={{ mb: 2 }}>
                Reminder: Support tables must start with <b>cost_</b>
            </Typography> */}
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Please confirm the Asset ID column name below, and load the Hydraulic COF scores associated with the Asset IDs. If the value is not there or incorrect, it can be manually entered. Please also ensure that the Asset ID column name in your CSV/XLSX file is identical to the column name in the base data. If it is not, please change the value before loading.
                </Typography>
                {showSuccess && <SuccessMessageComponent message="Columns have been confirmed!" compact />}

                <Typography variant="body2" sx={{ color: 555, textAlign: "left" }}>
                    Assetet ID Column*
                </Typography>
                <Autocomplete
                    freeSolo
                    options={assetIdOptions}
                    value={assetIdColumn}
                    inputValue={assetIdColumn}
                    onInputChange={(_, v) => setAssetIdColumn(v)}
                    onChange={(_, v) => setAssetIdColumn(v || "")}
                    renderInput={params => (
                        <TextField
                            {...params}
                            required
                        />
                    )}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "#F6FFE8",
                            height: 36,
                        },
                        "& .MuiOutlinedInput-input": {
                            padding: "8px 12px",
                        },
                        mb: 1,
                    }}
                    disabled={columnsLoading || assetIdOptions.length === 0}
                />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    *This must match the base data asset ID column “objectid”
                </Typography>
                <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleCostFilesChange}
                    accept=".csv,.xls,.xlsx"
                    style={{ display: "none" }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        textTransform: "none",
                        width: "fit-content",
                        minWidth: "132px",
                        alignSelf: "center",
                        backgroundColor: "#0373B5"
                    }}
                >
                    Select Files
                </Button>
                {costFiles.length > 0 && (
                    <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                        {costFiles.map(file => file.name).join(", ")}
                    </Typography>
                )}
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{
                        mt: 2,
                        textTransform: "none",
                        backgroundColor: "#0373B5",
                        width: "fit-content",
                        minWidth: "132px",
                        alignSelf: "center"
                    }}
                    disabled={
                        uploading ||
                        !assetIdColumn ||
                        !form.support_table ||
                        costFiles.length === 0
                    }
                >
                    {uploading ? "Uploading..." : "Upload"}
                </Button>
            </form>
            {(uploading || columnsLoading) && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <CircularProgress />
                </Box>
            )}
            {uploadStatus && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        maxWidth: 500,
                        mx: "auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6">Processing Status</Typography>
                    <Typography variant="body2">
                        Status: {uploadStatus}
                    </Typography>
                    <Typography variant="body2">
                        Message: {statusMessage}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default LoadCostScoringData;